import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BadgeCheck, Plus } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useServiceAdvancedStrategies,
  useServiceAdvancedEnabledSites,
  useDeleteServiceCompliance,
  COMPLIANCE_TYPE,
  type AdvancedStrategyListItem,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import styles from './ComplianceValidationTab.module.css';

const muted = (v: React.ReactNode) => <span className={styles.muted}>{v}</span>;

/**
 * AI-WAF → Compliance Validation. Lists HTTP Protocol Compliance rules
 * (waf_advanced_strategy type 8 — reads via the advanced endpoints) with add /
 * edit / delete (writes via the separate waf_compliance surface).
 */
export function ComplianceValidationTab() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const base = `/services/waap/${serviceId}`;
  const { show } = useToast();
  const [pendingDelete, setPendingDelete] = useState<AdvancedStrategyListItem | null>(null);

  const { data: rules = [], isLoading } = useServiceAdvancedStrategies(serviceId ?? '', COMPLIANCE_TYPE);
  const { data: sites = [] } = useServiceAdvancedEnabledSites(serviceId ?? '');

  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of sites) m.set(s.id, s.domain);
    return (id: number) => m.get(id) ?? `#${id}`;
  }, [sites]);

  const del = useDeleteServiceCompliance(serviceId ?? '', {
    onSuccess: () => {
      show({ tone: 'success', title: 'Compliance rule deleted' });
      setPendingDelete(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });

  const columns = useMemo<ColumnDef<AdvancedStrategyListItem>[]>(
    () => [
      { accessorKey: 'id', header: 'ID', cell: ({ row }) => muted(row.original.id) },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <span className={styles.name}>{row.original.name}</span>,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => row.original.description || muted('—'),
      },
      {
        id: 'website',
        header: 'Website',
        cell: ({ row }) => {
          const ids = row.original.sites ?? [];
          return ids.length ? ids.map(siteName).join(', ') : muted('—');
        },
      },
      {
        id: 'actions',
        header: 'Operation',
        cell: ({ row }) => (
          <div className={styles.actions}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`${base}/aiwaf/compliance/${row.original.id}`)}
            >
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPendingDelete(row.original)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, siteName],
  );

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs trail={[{ label: 'AI-WAF' }, { label: 'Compliance Validation' }]} />
        }
        title="Compliance Validation"
        description="Validate that requests conform to HTTP protocol limits — oversized or malformed requests are blocked."
        actions={
          <Button
            startIcon={<Plus size={16} />}
            onClick={() => navigate(`${base}/aiwaf/compliance/new`)}
          >
            Add HTTP Protocol Compliance Rule
          </Button>
        }
      />

      <Section
        title="HTTP Protocol Compliance Rules"
        actions={
          <StatusPill tone={rules.length ? 'info' : 'neutral'}>{rules.length} rules</StatusPill>
        }
      >
        {isLoading ? (
          <div className={styles.skeletonStack}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 48 }} />
            ))}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={rules}
            emptyState={
              <EmptyState
                size="sm"
                icon={<BadgeCheck size={20} />}
                title="No compliance rules"
                description="Add a rule to enforce HTTP protocol limits on incoming requests."
              />
            }
          />
        )}
      </Section>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete compliance rule?"
        description={`Delete "${pendingDelete?.name}"? This removes the protocol limits from the websites it applies to. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => pendingDelete && del.mutate(pendingDelete.id)}
      />
    </Container>
  );
}
